import { Filter, LifeInsuranceStep, OfferType, Product, RedirectType } from 'even-ts-static'
import * as React from 'react'
import { connect } from 'react-redux'

import {
  fetchZipcodeFromIP,
  lifeInsuranceOfferDeselected,
  lifeInsurancePhoneCallRequested,
  redirectToOffer,
  stepSelected,
  updateLead,
} from 'common/actions'
import { setLifeInsuranceResultsView } from 'embed/actions'
import { applicationFieldChanged, applicationFieldRegistered } from 'common/actions/application'
import { applicationLifeInsurancePersonalInformationIsValidSelector } from 'embed/selectors/application'
import { FieldDataValue, LifeInsuranceResultsView, MultiProductDispatch, RateTable } from 'embed/constants'
import Api, { ApiResponse } from 'common/services/Api'
import Parent from 'common/services/Parent'
import {
  captureGetRateTablesError,
  captureOfferRedirectUrlParseUuidError,
  captureRefreshRateTablesError,
} from 'embed/constants/errors'
import { LifeInsuranceOffer, LifeInsuranceProductSubType } from 'embed/constants/models'

import { MultiProductState } from 'embed/reducers'

import { lifeInsuranceRatesLeadUuidSelector, lifeInsuranceRatesUuidSelector } from 'embed/selectors/rates'
import { LifeInsurancePersonalInfo } from './index'

interface StoreProps {
  isValid: boolean
  initialSelectedOffer?: LifeInsuranceOffer
  customizeable: boolean
  requestACall: boolean
  rateTableResultsUuid: string
  rateTableLeadUuid: string
}

interface DispatchProps {
  onBackButtonClick: () => void
  dispatchApplicationFieldChanged: (filter: Filter, value: FieldDataValue) => void
  dispatchApplicationFieldRegistered: (filter: Filter) => void
  dispatchStepSelectedPersonalInfoConfirmation: (offer: LifeInsuranceOffer) => void
  dispatchRedirectToOffer: (offer: LifeInsuranceOffer) => void
  dispatchUpdateLead: () => Promise<void | ApiResponse>
  dispatchFetchZipcodeFromIP: () => void
}

type Props = StoreProps & DispatchProps

const LifeInsurancePersonalInfoContainer: React.SFC<Props> = (props) => {
  const {
    dispatchApplicationFieldChanged,
    dispatchApplicationFieldRegistered,
    dispatchRedirectToOffer,
    dispatchStepSelectedPersonalInfoConfirmation,
    dispatchUpdateLead,
    dispatchFetchZipcodeFromIP,
    initialSelectedOffer,
    isValid,
    rateTableResultsUuid,
    rateTableLeadUuid,
  } = props
  if (!initialSelectedOffer) {
    return null
  }
  const [selectedOffer, setSelectedOffer] = React.useState(initialSelectedOffer)
  const [noOfferFound, setNoOfferFound] = React.useState(false)
  const [updateLeadFailed, setUpdateLeadFailed] = React.useState(false)
  const [coverageAmount, setCoverageAmount] = React.useState(selectedOffer?.details.policyAmount)
  const [duration, setDuration] = React.useState(selectedOffer?.details.termLength)
  const [loadingCustomizedOffer, setLoadingCustomizedOffer] = React.useState(false)
  const [loadingLeadUpdate, setLoadingLeadUpdate] = React.useState(false)

  let requestStartTime: Date

  const onCoverageAmountChange = (_, value) => {
    setCoverageAmount(parseInt(value, 10))
  }

  const onDurationChange = (_, value) => {
    setDuration(parseInt(value, 10))
  }

  const onApplyClick = async () => {
    if (isValid && !loadingCustomizedOffer && !loadingLeadUpdate) {
      dispatchApplicationFieldChanged(Filter.ConsentsToTCPA, true)

      if (!selectedOffer) {
        return
      }
      if (await updateLeadOrShowError()) {
        dispatchRedirectToOffer(selectedOffer)
      }
    }
  }

  const updateLeadOrShowError = async (updateLeadAttempts = 1): Promise<boolean | undefined> => {
    // Attempt to patch the lead 3 times. If it fails, show an error.
    setLoadingLeadUpdate(true)

    try {
      await dispatchUpdateLead()
      setLoadingLeadUpdate(false)
      setUpdateLeadFailed(false)
      return true
    } catch (error) {
      // Errors from the leads endpoint are already sent to Sentry in the updateLead action, so
      // just handling it here for the sake of updating the UI
      if (updateLeadAttempts < 3) {
        await updateLeadOrShowError(updateLeadAttempts + 1)
      } else {
        setLoadingLeadUpdate(false)
        setUpdateLeadFailed(true)
        return false
      }
    }
  }

  const onRequestACallClick = async () => {
    if (isValid) {
      dispatchApplicationFieldChanged(Filter.ConsentsToTCPA, true)

      if (await updateLeadOrShowError()) {
        // Fake an offer click

        const uuidRegExp = /[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}/
        const redirectUuidMatch = selectedOffer?.url.match(uuidRegExp)

        if (redirectUuidMatch !== null && redirectUuidMatch !== undefined) {
          const redirectUuid = redirectUuidMatch[0]
          await Api.get(`/originator/offerRedirects/${redirectUuid}`)
        } else if (selectedOffer) {
          captureOfferRedirectUrlParseUuidError(selectedOffer.url)
        }

        if (selectedOffer) {
          dispatchStepSelectedPersonalInfoConfirmation(selectedOffer)
        }
      }
    }
  }

  const fetchRateTable = async (data: { carrierUuid?: string; duration?: number; policyAmount?: number }) => {
    requestStartTime = new Date()
    return Api.post(`/lifeInsurance/rateTables/${rateTableResultsUuid}/customize`, {
      lifeInsuranceInformation: data,
      uuid: rateTableLeadUuid,
    })
      .then(handleResponse)
      .catch((error) => {
        captureGetRateTablesError(Product.LifeInsurance, error)
      })
  }

  const refreshRateTable = async (rateTableUuid) => {
    try {
      const response = await Api.get(`/originator/rateTables/${rateTableUuid}`)
      handleResponse(response)
    } catch (error) {
      captureRefreshRateTablesError(Product.LifeInsurance, rateTableUuid, error)
    }
  }

  const handleResponse = (response: ApiResponse<RateTable>) => {
    const rateTableUuid = response.data.uuid
    if (!response.data.pendingResponses?.length && !response.data.pendingOriginators?.length) {
      if (response.data.lifeInsuranceOffers.length > 0) {
        setSelectedOffer(response.data.lifeInsuranceOffers[0])
        setNoOfferFound(false)
      } else {
        setNoOfferFound(true)
      }

      setLoadingCustomizedOffer(false)
    } else {
      // Poll every 100ms for 4 seconds, then poll at 1s
      const pollRate = requestStartTime.getTime() + 4000 > new Date().getTime() ? 100 : 1000
      setTimeout(() => refreshRateTable(rateTableUuid), pollRate)
    }
  }

  React.useEffect(() => {
    if (
      coverageAmount !== selectedOffer?.details.policyAmount ||
      duration !== selectedOffer?.details.termLength ||
      noOfferFound
    ) {
      setLoadingCustomizedOffer(true)
      fetchRateTable({ policyAmount: coverageAmount, duration, carrierUuid: selectedOffer?.partner.uuid }).catch(
        (error) => {
          captureGetRateTablesError(Product.LifeInsurance, error)
        },
      )
    }
  }, [coverageAmount, duration])

  React.useEffect(() => {
    dispatchApplicationFieldRegistered(Filter.ConsentsToTCPA)
  }, [])

  React.useEffect(Parent.scrollToFrame, [])

  React.useEffect(() => {
    dispatchFetchZipcodeFromIP()
  }, [])

  return selectedOffer ? (
    <LifeInsurancePersonalInfo
      {...props}
      coverageAmount={coverageAmount}
      duration={duration}
      loadingCustomizedOffer={loadingCustomizedOffer}
      loadingLeadUpdate={loadingLeadUpdate}
      noOfferFound={noOfferFound}
      onApplyClick={onApplyClick}
      onCoverageAmountChange={onCoverageAmountChange}
      onDurationChange={onDurationChange}
      onRequestACallClick={onRequestACallClick}
      selectedOffer={selectedOffer}
      updateLeadFailed={updateLeadFailed}
    />
  ) : null
}

const mapStateToProps = (state: MultiProductState) => {
  const isValid = applicationLifeInsurancePersonalInformationIsValidSelector(state)

  const initialSelectedOffer = state.rates.selected.life_insurance
  const isAdbOffer = initialSelectedOffer?.productSubType === LifeInsuranceProductSubType.AccidentalDeathBenefits
  const onlineAppAvailable = initialSelectedOffer?.details?.onlineAppAvailable
  const rateTableResultsUuid = lifeInsuranceRatesUuidSelector(state)
  const rateTableLeadUuid = lifeInsuranceRatesLeadUuidSelector(state)

  const customizeable = !isAdbOffer
  const requestACall = !onlineAppAvailable

  return {
    customizeable,
    isValid,
    requestACall,
    initialSelectedOffer: state.rates.selected.life_insurance,
    rateTableResultsUuid,
    rateTableLeadUuid,
  }
}

const mapDispatchToProps = (dispatch: MultiProductDispatch) => ({
  onBackButtonClick: () => {
    dispatch(lifeInsuranceOfferDeselected())
    dispatch(setLifeInsuranceResultsView(LifeInsuranceResultsView.Recommended))
  },
  dispatchApplicationFieldChanged: (filter: Filter, value: FieldDataValue) =>
    dispatch(applicationFieldChanged(Product.LifeInsurance, filter, value)),
  dispatchApplicationFieldRegistered: (filter: Filter) =>
    dispatch(applicationFieldRegistered(Product.LifeInsurance, filter)),
  dispatchStepSelectedPersonalInfoConfirmation: (offer: LifeInsuranceOffer) => {
    dispatch(stepSelected(Product.LifeInsurance, LifeInsuranceStep.PersonalInfoConfirmation))
    dispatch(lifeInsurancePhoneCallRequested(offer))
  },
  dispatchRedirectToOffer: (offer: LifeInsuranceOffer) =>
    dispatch(redirectToOffer(offer, OfferType.LifeInsurance, RedirectType.MANUAL)),
  dispatchUpdateLead: () => dispatch(updateLead(Product.LifeInsurance)),
  dispatchFetchZipcodeFromIP: () => dispatch(fetchZipcodeFromIP(Product.LifeInsurance, false)),
})

export const ConnectedLifeInsurancePersonalInfoContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(LifeInsurancePersonalInfoContainer)
